// src/api/composants.api.ts — SCE Issues
// La liste des composants NVS (06/10) : « ListeNVS.xlsx », posée par Guillaume
// dans « Déclarations NC-S › Liste NVS » (site OP_SCE). Lue pour chercher un
// composant dans la déclaration NC-S ; un composant nouveau y est AJOUTÉ à
// l'envoi de la NC-S. Règles : utils/composants.ts.
//
// ─── Lecture : 470 ko, gardés dans le navigateur ───
//
// Une petite requête lit la version du fichier (eTag) ; il n'est retéléchargé
// (et relu dans un worker) que s'il a changé — un nouvel export de l'ERP
// déposé à la place, ou un ajout. Les ~15 000 composants sont gardés dans le
// localStorage, sous une forme compacte.
//
// ─── Ajout : au mieux ───
//
// Pas de liste SharePoint dédiée (choix de Guillaume) : on récrit le
// classeur, à condition qu'il soit encore à la version lue (If-Match). S'il
// est ouvert dans Excel, ou a bougé entre-temps : tant pis, l'ajout est
// abandonné sans gêner la NC-S (« il n'y a pas de raison qu'il soit ouvert »).

import { GraphApiError, graphFetch, graphGet } from './graphClient'
import { DOSSIER_NCS } from './declarationNcs.api'
import { lireComposantsHorsInterface } from './lectureHorsInterface'
import { ajouterComposantAuClasseur, composantDuNvs, nvsAjoutable, type Composant } from '@/utils/composants'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''

/** Le classeur, dans le dossier des déclarations NC-S. */
export const CHEMIN_LISTE_NVS = `${DOSSIER_NCS}/Liste NVS/ListeNVS.xlsx`

const CLE = 'sce-issues:composants'
const VERSION_CACHE = 1
const TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

interface ElementFichier {
  id: string
  eTag?: string
  '@microsoft.graph.downloadUrl'?: string
}

interface MemoireComposants {
  version: number
  eTag: string
  composants: Composant[]
}

function adresse(): string {
  return `/sites/${SITE_ID}/drive/root:/${CHEMIN_LISTE_NVS.split('/').map(encodeURIComponent).join('/')}`
}

function lireMemoire(): MemoireComposants | null {
  try {
    const m = JSON.parse(localStorage.getItem(CLE) ?? 'null') as MemoireComposants | null
    return m && m.version === VERSION_CACHE && Array.isArray(m.composants) ? m : null
  } catch {
    return null
  }
}

function ecrireMemoire(m: MemoireComposants): void {
  try {
    localStorage.setItem(CLE, JSON.stringify(m))
  } catch {
    // Trop gros ou interdit : on relira le fichier la prochaine fois.
  }
}

async function telecharger(element: ElementFichier): Promise<ArrayBuffer> {
  const url = element['@microsoft.graph.downloadUrl']
  if (!url) throw new GraphApiError('Téléchargement de la liste NVS refusé', 403)
  const reponse = await fetch(url)
  if (!reponse.ok) throw new GraphApiError(`Téléchargement de la liste NVS (${reponse.status})`, reponse.status)
  return reponse.arrayBuffer()
}

let lectureEnCours: Promise<Composant[]> | null = null

/** Les composants connus ; retéléchargés seulement si le fichier a changé. Partagée entre fenêtres. */
export function listeComposants(): Promise<Composant[]> {
  lectureEnCours ??= lire().finally(() => {
    lectureEnCours = null
  })
  return lectureEnCours
}

async function lire(): Promise<Composant[]> {
  const element = await graphGet<ElementFichier>(adresse())
  const memoire = lireMemoire()
  if (memoire && element.eTag && memoire.eTag === element.eTag) return memoire.composants
  const composants = await lireComposantsHorsInterface(await telecharger(element))
  ecrireMemoire({ version: VERSION_CACHE, eTag: element.eTag ?? '', composants })
  return composants
}

/**
 * Ajoute le composant au classeur s'il n'y est pas encore (NVS bien formé et
 * désignation saisie). Ne lève jamais : un échec est seulement noté.
 * @returns vrai si la ligne a été écrite.
 */
export async function ajouterComposantSiNouveau(nvs: string, designation: string): Promise<boolean> {
  if (!nvsAjoutable(nvs) || !designation.trim()) return false
  try {
    const element = await graphGet<ElementFichier>(adresse())
    const octets = await telecharger(element)
    const connus = await lireComposantsHorsInterface(octets.slice(0))
    if (composantDuNvs(connus, nvs)) return false
    const nouveau = await ajouterComposantAuClasseur(octets, { nvs, designation })
    const session = await graphFetch<{ uploadUrl: string }>(
      `/sites/${SITE_ID}/drive/items/${element.id}/createUploadSession`,
      { method: 'POST', headers: { 'If-Match': element.eTag ?? '*' }, body: { item: { '@microsoft.graph.conflictBehavior': 'replace' } } },
    )
    const envoi = await fetch(session.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Range': `bytes 0-${nouveau.byteLength - 1}/${nouveau.byteLength}`, 'Content-Type': TYPE_XLSX },
      body: nouveau,
    })
    if (!envoi.ok) throw new GraphApiError(`Envoi de la liste NVS (${envoi.status})`, envoi.status)
    // La mémoire du navigateur le connaît tout de suite ; sa version sera relue au prochain usage.
    const memoire = lireMemoire()
    if (memoire) {
      ecrireMemoire({ ...memoire, eTag: '', composants: [...memoire.composants, [nvs.trim().toUpperCase(), designation.trim(), '']] })
    }
    return true
  } catch (err) {
    console.warn('[composants] ajout à la liste NVS abandonné :', err)
    return false
  }
}
